/**
 * IdentityInstanceDetailPageV2 — Stage 4-2.
 *
 * Per-instance identity detail page. Mounted at
 * /identity/instances/:instanceId. Reads the instance's identity
 * config + override state, displays inherited-vs-overridden values
 * side-by-side, wires the InstanceOverrideEditSlideout for write.
 *
 * Follows the IdentityTenantDetailPageV2 pattern.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Server, AlertTriangle, ArrowUp, Pencil, RefreshCw, Network, Clock, Settings } from 'lucide-react';
import { V2PageShell } from '../components/v2/V2PageShell';
import { Card } from '../components/v2/Card';
import { Button } from '../components/v2/Button';
import { Pill } from '../components/v2/Pill';
import { EmptyState } from '../components/v2/EmptyState';
import { Skeleton } from '../components/v2/Skeleton';
import { InstanceOverrideEditSlideout } from '../components/v2/identity/InstanceOverrideEditSlideout';
import { getIdentityInstance } from '../services/api';

export default function IdentityInstanceDetailPageV2() {
  const { instanceId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editSlideout, setEditSlideout] = useState({ open: false, mode: 'add' });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const classify = (msg) => {
      let status = null;
      const m = /^HTTP (\d{3}):/.exec(msg);
      if (m) status = Number(m[1]);
      else if (/instance_not_found/.test(msg)) status = 404;
      return { message: msg, status };
    };
    getIdentityInstance(instanceId).then((res) => {
      if (cancelled) return;
      if (res?.ok === false) setError(classify(res.error || 'Identity API error'));
      else setData(res?.data || null);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [instanceId]);

  if (loading) {
    return (
      <V2PageShell title="Loading…" backTo="/identity" backLabel="Back to Identity">
        <Skeleton height={80} />
        <div className="mt-3 grid grid-cols-2 gap-3">
          <Skeleton height={120} /><Skeleton height={120} />
        </div>
      </V2PageShell>
    );
  }

  if (error) {
    const is404 = error.status === 404;
    return (
      <V2PageShell title={is404 ? 'Instance not found' : 'Could not load instance'}
                   backTo="/identity" backLabel="Back to Identity">
        <Card>
          <EmptyState
            icon={<AlertTriangle />}
            title={is404 ? 'No instance with that id' : 'Identity API error'}
            description={is404
              ? `The identifier "${instanceId}" is not in the registry, or it's outside your org scope.`
              : error.message}
            primaryAction={
              <Button variant="primary" size="sm" onClick={() => navigate('/identity')}>
                Back to Identity
              </Button>
            }
          />
        </Card>
      </V2PageShell>
    );
  }

  const ov = data.override || {};
  const def = data.orgDefaults || {};

  // Build the four dimension rows: SVID TTL, rotation window, JWT TTL, SPIRE endpoint.
  const dims = [
    { key: 'svidTtl',      label: 'SVID TTL',
      icon: Clock,
      overrideValue: ov.svidTtlSeconds != null ? `${ov.svidTtlSeconds}s` : null,
      orgValue:      `${def.svidTtlSeconds}s` },
    { key: 'rotation',     label: 'Rotation window',
      icon: RefreshCw,
      overrideValue: ov.rotationWindowFraction != null ? `${ov.rotationWindowFraction}` : null,
      orgValue:      `${def.rotationWindowFraction}` },
    { key: 'jwtSvidTtl',   label: 'JWT-SVID TTL',
      icon: Clock,
      overrideValue: ov.jwtSvidTtlSeconds != null ? `${ov.jwtSvidTtlSeconds}s` : null,
      orgValue:      `${def.jwtSvidTtlSeconds}s` },
    { key: 'spireEndpoint',label: 'SPIRE endpoint',
      icon: Server,
      overrideValue: ov.spireEndpoint || null,
      orgValue:      'Org cluster (inherited)' },
  ];

  return (
    <V2PageShell
      title={data.name}
      subtitle={`${data.tier || 'CUSTOM'} · ${data.status} · ${
        data.hasOverride ? 'has identity overrides' : 'fully inherits tenant + org'
      }`}
      backTo={`/identity/tenants/${data.tenantId}`}
      backLabel={`Back to ${data.tenantName || 'tenant'}`}
      tags={
        <>
          <Pill tone="success" size="sm">Instance</Pill>
          <Pill tone="action" size="sm" dot>SPIFFE plane</Pill>
          {data.isProduction && <Pill tone="trust" size="sm">Production</Pill>}
          {data.hasOverride && <Pill tone="warning" size="sm">override</Pill>}
        </>
      }
      actions={
        <>
          {data.hasOverride && (
            <Button variant="secondary" size="sm"
                    onClick={() => setEditSlideout({ open: true, mode: 'remove' })}>
              Remove override
            </Button>
          )}
          <Button variant="primary" size="sm" iconLeft={<Settings size={14} />}
                  onClick={() => setEditSlideout({ open: true, mode: 'add' })}>
            {data.hasOverride ? 'Edit override' : 'Install override'}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        {dims.map((d) => {
          const Icon = d.icon;
          const isOverride = d.overrideValue != null;
          return (
            <Card key={d.key}>
              <Card.Header
                title={<><Icon size={16} className="inline -mt-0.5 mr-1.5 text-v2-action" aria-hidden="true" />{d.label}</>}
                actions={isOverride
                  ? <Pill tone="trust"  size="sm"><Pencil  size={11} className="inline -mt-0.5 mr-1" aria-hidden="true" />Override</Pill>
                  : <Pill tone="action" size="sm"><ArrowUp size={11} className="inline -mt-0.5 mr-1" aria-hidden="true" />Inherited</Pill>}
              />
              <Card.Body>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10.5px] uppercase tracking-wide text-v2-ink-muted font-semibold mb-1">
                      {isOverride ? 'Override value' : 'Resolved value'}
                    </div>
                    <div className="text-[13.5px] text-v2-ink font-semibold">
                      {isOverride ? d.overrideValue : d.orgValue}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10.5px] uppercase tracking-wide text-v2-ink-muted font-semibold mb-1">
                      Organization default
                    </div>
                    <div className={'text-[13.5px] ' + (isOverride ? 'text-v2-ink-muted line-through' : 'text-v2-ink-secondary')}>
                      {d.orgValue}
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          );
        })}
      </div>

      <InstanceOverrideEditSlideout
        isOpen={editSlideout.open}
        mode={editSlideout.mode}
        instanceId={data.id}
        instanceName={data.name}
        onClose={() => setEditSlideout({ open: false, mode: 'add' })}
        onSaved={() => { window.location.reload(); }}
      />
    </V2PageShell>
  );
}
